import {
  useServiceWorkerPersistedStore,
  useServiceWorkerStore,
} from "features/ServiceWorker";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "shared/ui";
import NewPageContentToast from "shared/ui/NewPageContentToast/NewPageContentToast";
import meta from "meta.json";
import { usePublicFiles } from "shared/api/hooks/usePublicFiles";

export const withServiceWorker = (Component) => () => {
  const { t } = useTranslation("Shared");
  const isUpdateAvailable = useServiceWorkerStore(
    (state) => state.isUpdateAvailable
  );
  const isCacheCleared = useServiceWorkerPersistedStore(
    (state) => state.isCacheCleared
  );

  const { data } = usePublicFiles("meta.json", {
    refreshInterval: 1000 * 30,
    revalidateOnMount: false,
  });

  // The following useEffect is created as an emergency solution in case of buildTimestamps mismatch
  // between server and client, and allows you to change the buildTimestamp on the server in the meta.json
  // file which will clear the cache and unregister all service workers.
  useEffect(() => {
    const clientTimestamp = meta.buildTimestamp;
    const serverTimestamp = data?.buildTimestamp;

    if (serverTimestamp && clientTimestamp) {
      if (
        serverTimestamp !== clientTimestamp &&
        !isCacheCleared &&
        !isUpdateAvailable
      ) {
        console.debug(
          "Build timestamps mismatch. Clearing cache and unregistering all current service workers..."
        );
        Promise.all([navigator.serviceWorker.getRegistrations(), caches.keys()])
          .then(([registrations, cacheNames]) => {
            for (let registration of registrations) {
              registration.unregister();
            }
            cacheNames.forEach((cacheName) => {
              caches.delete(cacheName);
            });
            useServiceWorkerPersistedStore.setState({ isCacheCleared: true });
            useServiceWorkerStore.setState({ isUpdateAvailable: true });
          })
          .catch(console.error);
      } else if (serverTimestamp === clientTimestamp) {
        useServiceWorkerPersistedStore.setState({ isCacheCleared: false });
      }
    }
  }, [data, isCacheCleared, isUpdateAvailable]);

  const reloadPage = () => {
    useServiceWorkerStore.setState({ isUpdateAvailable: false });
    window.location.reload();
  };

  useEffect(() => {
    const toastId = "new-page-version";
    if (isUpdateAvailable) {
      toast(<NewPageContentToast />, {
        closeButton: (
          <Button variant="link" size="sm" onClick={reloadPage}>
            {t("buttons.refresh")}
          </Button>
        ),
        toastId,
        autoClose: false,
        position: "bottom-left",
        draggable: false,
        closeOnClick: false,
      });
    } else {
      toast.dismiss(toastId);
    }
  }, [isUpdateAvailable]);

  return <Component />;
};
